import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import { Switch } from '@/src/shadComponents/ui/switch';
import '../tokens.css';
import wordmark from '../assets/photostat-wordmark.png';

/*
 * Login v2 — the front door in the v2 design language.
 * Same auth logic as the classic Login (credentials + Google Sign-In +
 * trust-this-device persist); new presentation. Auth screens are the
 * design system's exception to underline inputs: large boxed fields
 * with a brand focus ring and a brand CTA. Standalone .psv2 page; dark
 * mode follows the system preference. Fixes the classic screen's
 * missing type="password".
 */

const GOOGLE_CLIENT_ID =
    '475304023009-uaqeorfcb9d09jill84neov7bde5ve3m.apps.googleusercontent.com';

function greeting(): string {
    const h = new Date().getHours();
    if (h < 5) return 'Working late.';
    if (h < 12) return 'Good morning.';
    if (h < 17) return 'Good afternoon.';
    return 'Good evening.';
}

export default function LoginV2() {
    const navigate = useNavigate();
    const location = useLocation();
    const { signin, signInGoogle, persist, setPersist } = useAuth();
    const [feedback, setFeedback] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [dark, setDark] = useState(
        () =>
            window.matchMedia &&
            window.matchMedia('(prefers-color-scheme: dark)').matches,
    );

    // @ts-ignore
    const from = location.state?.from?.pathname || '/';
    const ready = username.trim().length > 0 && password.length > 0;

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        localStorage.setItem('persist', String(persist));
    }, [persist]);

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!ready || submitting) return;

        setSubmitting(true);
        setFeedback(null);
        try {
            const authenticated = await signin(username, password);
            if (authenticated) {
                navigate(from, { replace: true });
            } else {
                setFeedback(
                    'That didn’t work — check your username and password.',
                );
            }
        } catch (e) {
            setFeedback(
                'That didn’t work — check your username and password.',
            );
        } finally {
            setSubmitting(false);
        }
    }

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            // @ts-ignore
            google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                // @ts-ignore
                callback: async (jwt: { credential: string }) => {
                    const authenticated = await signInGoogle(jwt.credential);
                    if (authenticated) {
                        navigate(from, { replace: true });
                    } else {
                        setFeedback(
                            'That Google account isn’t in Photostat yet.',
                        );
                    }
                },
            });
            // @ts-ignore
            google.accounts.id.renderButton(
                document.getElementById('v2GoogleButton'),
                { theme: 'outline', size: 'large', shape: 'pill', width: 320 },
            );
            // @ts-ignore
            google.accounts.id.prompt();
        };
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={
                'psv2 flex min-h-screen items-center justify-center px-5' +
                (dark ? ' dark' : '')
            }
        >
            <main className="w-full max-w-[340px] pb-16">
                {/* black artwork — invert to white in dark mode */}
                <img
                    src={wordmark}
                    alt="photostat"
                    className="mx-auto mb-7 h-auto w-[190px] dark:invert"
                />

                <h1 className="text-center text-[23px] font-bold tracking-[-0.02em]">
                    {greeting()}
                </h1>
                <p className="ps-label mb-8 mt-2 text-center">
                    Sign in to Photostat
                </p>

                <form
                    onSubmit={handleSubmit}
                    noValidate
                    className="flex flex-col gap-3"
                >
                    <input
                        name="username"
                        aria-label="Username"
                        placeholder="Username"
                        className="ps-field"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <input
                        name="password"
                        type="password"
                        aria-label="Password"
                        placeholder="Password"
                        className="ps-field"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />

                    {feedback && (
                        <p
                            role="alert"
                            className="text-[12.5px] font-semibold text-attention"
                        >
                            {feedback}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={!ready || submitting}
                        className={
                            'mt-1 w-full cursor-pointer rounded-full border-0 py-3 text-center font-[inherit] text-[14px] font-semibold text-brand-foreground transition-colors ' +
                            (ready && !submitting
                                ? 'bg-brand hover:opacity-95'
                                : 'cursor-default bg-brand/40')
                        }
                    >
                        {submitting ? 'Signing in…' : 'Sign in'}
                    </button>

                    <label className="mt-2 flex items-center justify-center gap-2.5 text-[12.5px] text-muted-foreground">
                        <Switch
                            checked={persist}
                            onCheckedChange={() =>
                                setPersist((prev: boolean) => !prev)
                            }
                        />
                        Trust this device for 30 days
                    </label>
                </form>

                <div className="my-6 flex items-center gap-3">
                    <span className="h-px flex-1 bg-line" />
                    <span className="ps-label">or</span>
                    <span className="h-px flex-1 bg-line" />
                </div>

                <div id="v2GoogleButton" className="flex justify-center" />
            </main>
        </div>
    );
}
